'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useAuth } from '@/providers/userContext';
import { haversineMiles } from '@/shared/geo';
import { useExplore } from './ExploreProvider';
import { ExploreHeader } from './ExploreHeader';
import { ListView } from './ListView';
import { ViewToggle } from './ViewToggle';
import ExploreListCard from './ExploreListCard';
import { LocationDeniedModal } from './LocationDeniedModal';
import { HeartsScopeToggle } from './HeartsScopeToggle';
import { SearchInput, FilterButton, LocationButton, SearchBarContainer } from './SearchBar';
import PromotionLink from './PromotionLink';
import Styles from './Explore.module.css';
const ExploreMapView = dynamic(() => import('./ExploreMapView'), {
  ssr: false,
  loading: () => <div className="h-full w-full bg-slate-100" />
});
function sortForMapList(items, sortMode, userLocation) {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    if (sortMode === 'distance' && userLocation) {
      const aCoords = a.location ? [a.location.lat, a.location.lng] : null;
      const bCoords = b.location ? [b.location.lat, b.location.lng] : null;
      if (!aCoords && !bCoords) return 0;
      if (!aCoords) return 1;
      if (!bCoords) return -1;
      const aDist = haversineMiles([userLocation.lat, userLocation.lng], aCoords);
      const bDist = haversineMiles([userLocation.lat, userLocation.lng], bCoords);
      return aDist - bDist;
    }
    const aName = (a.name || '').toLowerCase();
    const bName = (b.name || '').toLowerCase();
    return aName.localeCompare(bName, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  });
}
function DesktopMapSidebar() {
  const $ = _c(35);
  const {
    user
  } = useAuth();
  const {
    listItems,
    markers,
    selectedItem,
    setSelectedItem,
    sortMode,
    userLocation,
    heartsProgress
  } = useExplore();
  let t0;
  if ($[0] !== heartsProgress) {
    t0 = heartsProgress?.total ? Math.min(100, Math.round(heartsProgress.visited / heartsProgress.total * 100)) : 0;
    $[0] = heartsProgress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const progressPercent = t0;
  let t1;
  if ($[2] !== markers) {
    t1 = new Set((markers || []).map(_temp));
    $[2] = markers;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const markerIds = t1;
  let t2;
  if ($[4] !== listItems || $[5] !== markerIds || $[6] !== sortMode || $[7] !== userLocation) {
    let t3;
    if ($[9] !== markerIds) {
      t3 = item => markerIds.has(item.id);
      $[9] = markerIds;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    const itemsWithVisiblePins = (listItems || []).filter(t3);
    t2 = sortForMapList(itemsWithVisiblePins, sortMode, userLocation);
    $[4] = listItems;
    $[5] = markerIds;
    $[6] = sortMode;
    $[7] = userLocation;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const displayItems = t2;
  let t3;
  let t4;
  if ($[11] !== selectedItem) {
    t3 = () => {
      if (!selectedItem?.id) {
        return;
      }
      const itemEl = document.getElementById(`map-list-item-${selectedItem.id}`);
      itemEl?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    };
    t4 = [selectedItem];
    $[11] = selectedItem;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== setSelectedItem) {
    t5 = item_0 => {
      setSelectedItem(item_0);
    };
    $[14] = setSelectedItem;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleMapItemClick = t5;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="space-y-2.5"><SearchBarContainer><SearchInput id="mapDesktopSidebarSearch" searchClass="right-0 pr-2 text-[#D9D9D9]" serchInputClass="pr-2 text-[15px] border border-[#E0E0E0] !shadow-none text-lg text-[#757575] leading-[1em]" /></SearchBarContainer><HeartsScopeToggle /></div>;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== displayItems || $[18] !== handleMapItemClick || $[19] !== selectedItem || $[20] !== userLocation) {
    let t8;
    if ($[22] !== handleMapItemClick || $[23] !== selectedItem || $[24] !== userLocation) {
      t8 = item_1 => {
        const isActive = selectedItem?.id === item_1.id;
        const shouldFade = selectedItem && !isActive;
        shouldFade ? "opacity-100" : "opacity-100";
        return <div key={item_1.id} id={`map-list-item-${item_1.id}`} className="transition-opacity opacity-100"><ExploreListCard item={item_1} userLocation={userLocation} view="map" isActive={isActive} onMapClick={handleMapItemClick} /></div>;
      };
      $[22] = handleMapItemClick;
      $[23] = selectedItem;
      $[24] = userLocation;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    t7 = displayItems.map(t8);
    $[17] = displayItems;
    $[18] = handleMapItemClick;
    $[19] = selectedItem;
    $[20] = userLocation;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[26] !== t7) {
    t8 = <div className="hidden lg:block absolute top-4 bottom-4 w-[410px] z-20"><div className="max-h-full rounded-[12px] bg-white/85 backdrop-blur-sm shadow-[-2px_2px_20px_0_rgba(0,0,0,0.1)] p-3 flex flex-col">{t6}<div className="mt-5 overflow-y-auto px-5 mx-[-1.25rem] no-scrollbar">{t7}</div></div></div>;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== heartsProgress || $[29] !== progressPercent || $[30] !== user) {
    t9 = user && <div className="w-[207px] h-[37px] absolute top-[20px] right-5 xl:right-[3%] 2xl:right-[7.4%] hidden lg:block"><div className="z-101 flex items-center gap-1 justify-center w-full text-base font-medium text-black leading-[36px] relative z-10 text-center"><span>{heartsProgress.visited} / {heartsProgress.total}</span><span>Hearts Visited</span></div><div className="z-100 absolute left-0 right-0 top-0 w-full h-full rounded-[100px] bg-white/85 shadow-[-2px_2px_20px_0_rgba(0,0,0,0.06)] backdrop-blur-[3.35px] overflow-hidden"><div className="h-full rounded-[100px] bg-[#00EAFD] transition-all duration-300" style={{
          width: `${progressPercent}%`
        }} /></div></div>;
    $[28] = heartsProgress;
    $[29] = progressPercent;
    $[30] = user;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t8 || $[33] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
}
function _temp(marker) {
  return marker.id;
}
function MobileMapPeekList() {
  const $ = _c(40);
  const {
    user
  } = useAuth();
  const {
    listItems,
    markers,
    heartsProgress,
    sortMode,
    userLocation,
    setViewMode,
    setSelectedItem
  } = useExplore();
  const touchStartY = useRef(0);
  let t0;
  if ($[0] !== markers) {
    t0 = new Set((markers || []).map(_temp2));
    $[0] = markers;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const markerIds = t0;
  let t1;
  if ($[2] !== listItems || $[3] !== markerIds || $[4] !== sortMode || $[5] !== userLocation) {
    let t2;
    if ($[7] !== markerIds) {
      t2 = item => markerIds.has(item.id);
      $[7] = markerIds;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    const itemsWithVisiblePins = (listItems || []).filter(t2);
    t1 = sortForMapList(itemsWithVisiblePins, sortMode, userLocation);
    $[2] = listItems;
    $[3] = markerIds;
    $[4] = sortMode;
    $[5] = userLocation;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const displayItems = t1;
  let t2;
  if ($[9] !== heartsProgress.total || $[10] !== heartsProgress.visited) {
    t2 = heartsProgress.total ? Math.min(100, Math.round(heartsProgress.visited / heartsProgress.total * 100)) : 0;
    $[9] = heartsProgress.total;
    $[10] = heartsProgress.visited;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const progressPercent = t2;
  let t3;
  if ($[12] !== setSelectedItem) {
    t3 = item_0 => {
      setSelectedItem(item_0);
    };
    $[12] = setSelectedItem;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleMapItemClick = t3;
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      touchStartY.current = e.touches[0].clientY;
    };
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleTouchStart = t4;
  let t5;
  if ($[15] !== setViewMode) {
    t5 = e_0 => {
      const diff = touchStartY.current - e_0.changedTouches[0].clientY;
      if (diff > 30) {
        setViewMode("list");
      }
    };
    $[15] = setViewMode;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleTouchEnd = t5;
  let t6;
  if ($[17] !== setViewMode) {
    t6 = <button type="button" onClick={() => setViewMode("list")} className="mx-auto h-1.5 w-12 rounded-full bg-[#D3D3D3]" aria-label="Switch to list view" />;
    $[17] = setViewMode;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== handleTouchEnd || $[20] !== t6) {
    t7 = <div className="flex items-center justify-between pt-2 pb-3" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>{t6}</div>;
    $[19] = handleTouchEnd;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== heartsProgress.total || $[23] !== heartsProgress.visited || $[24] !== progressPercent || $[25] !== user) {
    t8 = user && <div className="w-[207px] h-[37px] absolute -top-[45px] right-[17px] "><div className="flex items-center gap-1 justify-center w-full text-base font-medium text-black leading-[36px] relative z-10 text-center"><span>{heartsProgress.visited} / {heartsProgress.total}</span><span>Hearts Visited</span></div><div className="absolute left-0 right-0 top-0 w-full h-full rounded-[100px] bg-white/85 shadow-[-2px_2px_20px_0_rgba(0,0,0,0.06)] backdrop-blur-[3.35px] overflow-hidden"><div className="h-full rounded-[100px] bg-[#00EAFD] transition-all duration-300" style={{
          width: `${progressPercent}%`
        }} /></div></div>;
    $[22] = heartsProgress.total;
    $[23] = heartsProgress.visited;
    $[24] = progressPercent;
    $[25] = user;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== displayItems || $[28] !== handleMapItemClick || $[29] !== userLocation) {
    let t10;
    if ($[31] !== handleMapItemClick || $[32] !== userLocation) {
      t10 = item_1 => <ExploreListCard key={item_1.id} item={item_1} userLocation={userLocation} view="map" onMapClick={handleMapItemClick} />;
      $[31] = handleMapItemClick;
      $[32] = userLocation;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    t9 = displayItems.map(t10);
    $[27] = displayItems;
    $[28] = handleMapItemClick;
    $[29] = userLocation;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[34] !== t9) {
    t10 = <div className="h-[calc(100%-25px)] overflow-y-auto no-scrollbar bg-[#F9F9F9] rounded-t-2xl">{t9}</div>;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t7 || $[38] !== t8) {
    t11 = <><div className="lg:hidden absolute inset-x-0 bottom-0 z-20 pointer-events-none"><div className="pointer-events-auto h-[26dvh]">{t7}{t8}{t10}</div></div></>;
    $[36] = t10;
    $[37] = t7;
    $[38] = t8;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
}
function _temp2(marker) {
  return marker.id;
}
function ViewRenderer() {
  const $ = _c(10);
  const {
    viewMode,
    hasViewedMap
  } = useExplore();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h1 className="sr-only">Explore</h1>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== hasViewedMap || $[2] !== viewMode) {
    t1 = hasViewedMap && <div className={`absolute inset-0 2xl:h-[calc(100dvh-68px)] lg:h-[calc(100dvh-60px)] h-[calc(100dvh-52px)] transition-opacity duration-200 ${viewMode === "map" ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}><div className="container-wrap relative h-0 lg:hidden"><div className="2xl:pt-6.25 lg:pt-4 pt-3"><ExploreHeader searchWrapperClass="!relative !left-0 !top-0" id="searchBar" /></div></div><div className="absolute inset-0 container-wrap"><DesktopMapSidebar /><MobileMapPeekList /><FilterButton className="!absolute z-1 hidden lg:flex cursor-pointer border-none bg-white/85 backdrop-blur-sm shadow-[-2px_2px_20px_0_rgba(0,0,0,0.1)] top-[15px] left-[25em]" /><LocationButton className="absolute z-1 hidden lg:flex cursor-pointer border-none bg-white/85 backdrop-blur-sm shadow-[-2px_2px_20px_0_rgba(0,0,0,0.1)] top-[15px] left-[35.4em]" /></div><ExploreMapView className="h-full w-full" /></div>;
    $[1] = hasViewedMap;
    $[2] = viewMode;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `absolute inset-0 overflow-y-auto ${Styles.mobileNoScrollbar} transition-opacity duration-200 ${viewMode === "list" ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ListView />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <div className={t2}>{t3}</div>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <div className="flex-1 relative h-full 2xl:min-h-[calc(100dvh-68px)] lg:min-h-[calc(100dvh-60px)] min-h-[calc(100dvh-52px)]">{t0}{t1}{t4}</div>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
export function ExploreLayout(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  if (children) {
    let t1;
    if ($[0] !== children) {
      t1 = <div className="h-screen flex flex-col bg-neutral-100">{children}</div>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LocationDeniedModal />;
    t2 = <PromotionLink />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ExploreLayout.Content><ViewRenderer /></ExploreLayout.Content>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className={`${Styles.exploreWrapper} flex flex-col bg-neutral-100`}>{t1}{t2}{t3}<ExploreLayout.Footer><ViewToggle /></ExploreLayout.Footer></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

// Composable layout components
ExploreLayout.Content = function ExploreContent({
  children
}) {
  return <div className="flex-1 flex flex-col min-h-0">{children}</div>;
};
ExploreLayout.Footer = function ExploreFooter({
  children
}) {
  return <div className="flex-shrink-0">{children}</div>;
};